import type { CustomerPlaybookScore } from "@repo/app-types/entities/performanceCustomer";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import {
  buildTeammateDisplayNames,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { ExpandablePreview } from "@repo/ui/expandablePreview/expandablePreview";
import { Size } from "@repo/ui/helpers/helpers";
import {
  TeammateLabel,
  teammateLabelKind,
} from "@repo/ui/teammate/teammateLabel";
import { DateTime } from "luxon";
import { useId, useState, type FC } from "react";
import { Button as AriaButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { GradingSectionSummariesContainer } from "../teammatePerformance/gradingSectionSummariesContainer.js";

const SCORE_ROW_HEIGHT = 32;
// Collapsed, the list shows four and a half rows with the fade over the last
// two, so a list of four fits without collapsing.
const COLLAPSED_SCORE_ROW_COUNT = 4;
const COLLAPSED_SCORES_HEIGHT = 4.5 * SCORE_ROW_HEIGHT;
const SCORES_FADE_HEIGHT = 56;

export interface CustomerPlaybookScoresProps {
  scores: readonly CustomerPlaybookScore[];
  selectedPlaybookId: PlaybookId | null;
  selectedTeammateId: TeammateId | null;
  /** Pressing a row selects its playbook and teammate; pressing it again clears both. */
  onSelect: (
    playbookId: PlaybookId | null,
    teammateId: TeammateId | null,
  ) => void;
}

/** One row per playbook and teammate on the account, each filtering the timeline. */
export const CustomerPlaybookScores: FC<CustomerPlaybookScoresProps> = (
  props,
) => {
  const { scores, selectedPlaybookId, selectedTeammateId, onSelect } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const titleId = useId();

  // Split rows carry no scores, so a card of only split rows has nothing to show.
  if (scores.every((score) => score.is_split)) {
    return null;
  }

  const list = (
    <div className="isolate -mx-2 overflow-x-auto overscroll-x-none">
      <ul className="grid w-max min-w-full grid-cols-[8rem_10rem_minmax(max-content,1fr)_7rem] gap-x-4 text-xs sm:grid-cols-[10rem_10rem_minmax(max-content,1fr)_8rem]">
        {scores.map((score) => {
          const teammateName = buildTeammateDisplayNames(
            score.teammate,
          ).primaryDisplayName;
          const isActive =
            selectedPlaybookId === score.playbook_id &&
            selectedTeammateId === score.teammate.id;
          const isDimmed =
            (selectedPlaybookId !== null &&
              score.playbook_id !== selectedPlaybookId) ||
            (selectedTeammateId !== null &&
              score.teammate.id !== selectedTeammateId);
          const handleRowPress = () => {
            if (isActive) {
              onSelect(null, null);
            } else {
              onSelect(score.playbook_id, score.teammate.id);
            }
          };
          return (
            <li
              key={`${score.playbook_id}:${score.teammate.id}:${score.is_split}`}
              className={twMerge(
                "relative col-span-full grid h-8 grid-cols-subgrid items-center rounded-md bg-card transition-[background-color,opacity] delay-[25ms] duration-[350ms] hover:bg-gray-50 active:bg-gray-100 motion-reduce:transition-none",
                isDimmed &&
                  "opacity-20 duration-[150ms] hover:opacity-100 has-focus-visible:opacity-100",
              )}
            >
              <AriaButton
                aria-label={
                  isActive
                    ? "Clear playbook and teammate filters"
                    : `Filter by ${teammateName} and ${score.playbook_name}`
                }
                aria-pressed={isActive}
                onPress={handleRowPress}
                className="peer absolute inset-0 cursor-pointer rounded-md border-0 bg-transparent p-0 outline-hidden"
              />
              {/* The focus ring sits above the pinned cells, which would
                  otherwise cover the button's own outline. */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 z-20 rounded-md peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-600 peer-focus-visible:ring-inset"
              />
              <div className="pointer-events-none sticky left-0 z-10 flex h-8 min-w-0 items-center rounded-l-md bg-inherit px-2 after:absolute after:inset-y-0 after:-right-4 after:w-4 after:bg-inherit after:[mask-image:linear-gradient(to_right,black,transparent)] after:content-['']">
                <TeammateLabel
                  name={teammateName}
                  size={Size.SMALL}
                  kind={teammateLabelKind(score.teammate.type)}
                />
              </div>
              <span
                className="pointer-events-none min-w-0 truncate px-2 text-muted-foreground"
                title={score.playbook_name}
              >
                {score.playbook_name}
              </span>
              {/* Positioned so it paints above the row's absolute button. The
                  squares take the pointer for their tooltips; a click on one
                  bubbles here and presses the row like anywhere else. */}
              <div
                className="pointer-events-none relative min-w-0 px-2 whitespace-nowrap [&_[role=button]]:pointer-events-auto"
                onClick={handleRowPress}
              >
                {score.is_split ? (
                  <span className="text-muted-foreground">
                    Calls scored individually
                  </span>
                ) : score.assessment ? (
                  <GradingSectionSummariesContainer
                    assessment={score.assessment}
                    emptyState={
                      <span className="text-muted-foreground">
                        No applicable scoring
                      </span>
                    }
                    className="min-h-4"
                  />
                ) : (
                  <span className="text-muted-foreground">
                    No grading summary yet
                  </span>
                )}
              </div>
              <div className="pointer-events-none sticky right-0 z-10 flex h-8 items-center justify-end rounded-r-md bg-inherit px-2 whitespace-nowrap text-muted-foreground before:absolute before:inset-y-0 before:-left-4 before:w-4 before:bg-inherit before:[mask-image:linear-gradient(to_left,black,transparent)] before:content-['']">
                <time dateTime={score.latest_recording_at}>
                  Last call{" "}
                  {DateTime.fromISO(score.latest_recording_at).toFormat(
                    "MMM d",
                  )}
                </time>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <section
      aria-labelledby={titleId}
      className={twMerge(
        "relative isolate min-w-0 rounded-lg border border-border-light bg-card px-4 py-4 shadow-xs sm:px-5",
        PH_MASK_CLASS,
      )}
    >
      <h2
        id={titleId}
        className="mb-3 text-xs font-medium text-muted-foreground"
      >
        Playbook scores
      </h2>
      {scores.length > COLLAPSED_SCORE_ROW_COUNT ? (
        <ExpandablePreview
          isExpanded={isExpanded}
          onExpandedChange={setIsExpanded}
          collapsedHeight={COLLAPSED_SCORES_HEIGHT}
          fadeHeight={SCORES_FADE_HEIGHT}
          expandLabel="Show all"
        >
          {list}
        </ExpandablePreview>
      ) : (
        list
      )}
    </section>
  );
};
